import { LitElement, html, css, t, paths, unsafeHTML } from '@hh/global.js'

class HHBundleSummary extends LitElement {
  static properties = {
    currentStep: { type: Number },
    bundle: {
      type: Array,
      reflect: true,
    },
    steps: { type: Array },
    message: { type: String },
    boxImage: { type: String },
    currentItemsCount: { type: Number },
    packagingIncluded: { type: String },
    _isExpanded: {
      state: true,
    },
  }

  constructor() {
    super()
    this._isExpanded = false
  }

  onToggle() {
    this._isExpanded = !this._isExpanded
  }

  formatStep(n) {
    return n < 10 ? `0${n}` : `${n}`
  }

  changeStep(step) {
    this.dispatchEvent(new CustomEvent('changeStep', {
      detail: {
        step,
      },
      bubbles: true,
      composed: true,
    }))
  }

  get _steps() {
    return this.steps.map((step, index) => {
      const slots = []
      this.bundle[index].forEach((item) => {
        const product = this.steps[index].products.find((product) => product.variantId === item.variantId)
        for (let i = 0; i < item.qty; i++) {
          slots.push(product)
        }
      })
      const emptySlots = step.quantity - slots.length
      if (emptySlots > 0) {
        for (let i = 0; i < emptySlots; i++) {
          slots.push(null)
        }
      }
      return {
        ...step,
        slots,
        isFinished: emptySlots < 1,
      }
    })
  }

  get _completedSteps() {
    return this._steps.filter((step) => step.isFinished).length
  }

  get _completedStepsPercentage() {
    return (this._completedSteps / this.steps.length) * 100
  }

  get _selectedItemsPercentage() {
    return (this.currentItemsCount / this.steps[this.currentStep].quantity) * 100
  }

  get summaryPanelData() {
    const itemsSelectedLabel = t('sections.main-bundle.items_selected').replace('{{ count }}', this.currentItemsCount).replace('{{ total }}', this.steps[this.currentStep].quantity)

    if (this.currentStep === this.steps.length - 1 && this._steps.every((step) => step.isFinished)) {
      return {
        label: `${this.steps.length}/${this.steps.length} ${t('sections.main-bundle.steps_completed')}`,
        percent: this._selectedItemsPercentage,
      }
    }
    const label = this._steps[this.currentStep].isFinished ? t('sections.main-bundle.selections_complete') : itemsSelectedLabel
    return {
      label: `${t('sections.main-bundle.step')} ${this.currentStep + 1}: ${label}`,
      percent: this._selectedItemsPercentage,
    }
  }

  render() {
    return html`
      <div>
        <div class="bundle-summary__progress">
          <div class="bundle-summary__progress-label">
            ${this.currentStep + 1}/${this._steps.length} ${t('sections.main-bundle.steps_completed')}
          </div>
          <div class="bundle-summary__progress-bar">
            <div class="bundle-summary__progress-bg" style="width: ${this._completedStepsPercentage}%"></div>
          </div>
        </div>
        <div class="bundle-summary__wrapper">
          ${this.message ? html`
            <div class="bundle-summary__message">${this.message}</div>` : ''}
          <div class="bundle-summary ${this._isExpanded ? 'expanded' : ''}">
            <button @click="${this.onToggle}" class="bundle-summary__panel">
              <div class="bundle-summary__panel-label">
                ${unsafeHTML(paths.chevron)}
                ${t('sections.main-bundle.summary')}
              </div>
              <div class="bundle-summary__panel-title">
                ${this.summaryPanelData.label}
              </div>
              <div class="bundle-summary__progress-bar">
                <div class="bundle-summary__progress-bg" style="width: ${this.summaryPanelData.percent}%"></div>
              </div>
            </button>
            <div class="bundle-summary__title">
              ${t('sections.main-bundle.bundle_summary')}
            </div>
            <div class="bundle-summary__progress">
              <div class="bundle-summary__progress-label">
                ${this._completedSteps}/${this._steps.length} ${t('sections.main-bundle.steps_completed')}
              </div>
              <div class="bundle-summary__progress-bar">
                <div class="bundle-summary__progress-bg" style="width: ${this._completedStepsPercentage}%"></div>
              </div>
            </div>
            <div class="bundle-summary__steps">
              <div class="bundle-summary__steps-list">
                ${this.packagingIncluded === true ? html`
                  <div class="bundle-summary__box">
                    <img src="${this.boxImage}" />
                    <div class="bundle-summary__box-text">${unsafeHTML(t('sections.main-bundle.box_included_html'))}
                    </div>
                  </div>
                ` : ''}
                ${this._steps.map((step, index) =>
                  html`
                    <button ?disabled="${!step.isFinished}" @click="${() => this.changeStep(index)}"
                            class="bundle-summary__step ${step.isFinished ? 'active' : ''}">
                      <div class="bundle-summary__step-content">
                        <div class="bundle-summary__step-title">
                          ${this.formatStep(index + 1)}
                          <div>
                            ${step.title}
                            ${!step.isFinished ? html`
                              <div class="bundle-summary__step-label">(${t('sections.main-bundle.select')}
                                ${step.quantity})
                              </div>` : html`
                              <div class="bundle-summary__step-mark">${unsafeHTML(paths.checkmark)}</div>`}
                          </div>
                        </div>
                        <div class="bundle-summary__step-slots">
                          ${step.slots.map((slot, index) =>
                            html`
                              <div class="bundle-summary__slot ${slot ? 'active' : ''}">
                                ${slot ? html`<img class="bundle-summary__slot-image" src="${slot.image}"
                                                   alt="${slot.title}" />` : ''}
                              </div>`,
                          )}
                        </div>
                      </div>
                      <div class="bundle-summary__step-icon">
                        ${step.isFinished ? html`
                          <div class="bundle-summary__step-edit">${unsafeHTML(paths.pencil)}
                          </div>` : unsafeHTML(paths.plus)}
                      </div>
                    </button>`,
                )}
              </div>
            </div>
            <div class="bundle-summary__buttons">
              <slot></slot>
            </div>
          </div>
        </div>
      </div>
    `
  }

  static styles = css`
    .bundle-summary__step-slots {
      display: flex;
      flex-wrap: wrap;
      gap: var(--hh-spacing-xxs);
    }

    .bundle-summary__title {
      display: none;
      font: var(--hh-body-bold-lg-font);
      text-align: center;
      color: var(--hh-color-night);
      margin-bottom: var(--hh-spacing-md);
    }

    .bundle-summary__steps {
      max-height: 0;
      flex: 1;
      min-height: 0;
      overflow-y: auto;
      transition: max-height 0.5s cubic-bezier(0, 1, 0, 1);
      padding: 0 var(--hh-spacing-sm);
    }

    .bundle-summary.expanded .bundle-summary__steps {
      transition: max-height 1s ease-in-out;
      max-height: 2000px;
    }

    .bundle-summary__steps-list {
      display: flex;
      flex-direction: column;
      gap: var(--hh-spacing-sm);
      padding: var(--hh-spacing-sm) var(--hh-spacing-xs) var(--hh-spacing-sm) 0;
    }

    .bundle-summary__step {
      padding: var(--hh-spacing-sm);
      display: flex;
      gap: var(--hh-spacing-sm);
      align-items: center;
      border: 0.1rem dashed var(--hh-color-mid-grey);
      border-radius: 0.4rem;
      background: none;
    }

    .bundle-summary__step.active {
      border-style: solid;
      border-color: var(--hh-color-night);
    }

    .bundle-summary__step-title {
      font: var(--hh-body-bold-sm-font);
      letter-spacing: var(--hh-body-bold-sm-letter-spacing);
      color: var(--hh-color-brand-subdued);
      margin-bottom: var(--hh-spacing-xxs);
      display: flex;
      gap: var(--hh-spacing-sm);
      align-items: center;
    }

    .bundle-summary__step-title div {
      display: flex;
      align-items: center;
    }

    .bundle-summary__step-content{
      flex: 1;
    }

    .bundle-summary__step-icon {
      flex: none;
    }

    .bundle-summary__step-edit {
      width: 2.4rem;
      height: 2.4rem;
      border-radius: 50%;
      border: 0.1rem solid rgba(var(--hh-color-night-rgb), 0.18);
      display: flex;
      justify-content: center;
      align-items: center;
      color: var(--hh-color-night);
    }

    .bundle-summary__step-edit svg {
        width: 1rem;
        height: auto;
    }

    .bundle-summary__step-label {
      font: var(--hh-body-sm-font);
      letter-spacing: var(--hh-body-sm-letter-spacing);
      margin-left: var(--hh-spacing-xxs);
    }

    .bundle-summary__step-mark {
      color: var(--hh-color-success);
      margin-left: var(--hh-spacing-sm);
    }

    .bundle-summary__step-mark svg {
      height: 0.6rem;
      width: auto;
    }

    .bundle-summary__step-icon > svg {
      height: 0.8rem;
      width: 0.8rem;
    }

    .bundle-summary__progress-label {
      font: var(--hh-body-sm-font);
      letter-spacing: var(--hh-body-sm-letter-spacing);
      color: var(--hh-color-night);
      text-align: center;
      margin-bottom: var(--hh-spacing-sm);
    }

    .bundle-summary__progress{
      margin-bottom: var(--hh-spacing-2xl);
    }

    .bundle-summary .bundle-summary__progress {
      display: none;
    }

    .bundle-summary__slot {
      width: 2.9rem;
      height: 2.9rem;
      border-radius: 0.2rem;
      border: 0.1rem dashed var(--hh-color-brand-secondary);
      overflow: hidden;
    }

    .bundle-summary__slot.active {
      border-style: solid;
    }

    .bundle-summary__slot-image {
      width: 100%;
      height: 100%;
      display: block;
    }

    .bundle-summary__progress-bar {
      width: 100%;
      height: var(--hh-spacing-sm);
      border-radius: 6.6rem;
      border: 0.1rem solid rgba(var(--hh-color-night-rgb), 0.22);
      overflow: hidden;
    }

    .bundle-summary__progress-bg {
      height: 100%;
      background: var(--hh-color-orange);
    }

    .bundle-summary {
      position: fixed;
      z-index: 90001;
      bottom: 0;
      left: 0;
      width: 100%;
      background: var(--hh-color-day);
      border-top: 0.1rem solid var(--hh-color-mid-grey);
      border-bottom: 0.1rem solid var(--hh-color-mid-grey);
      display: flex;
      flex-direction: column;
      justify-content: end;
      max-height: 100%;
    }

    .bundle-summary__buttons {
      border-top: 0.1rem solid var(--hh-color-mid-grey);
      padding: var(--hh-spacing-sm) var(--hh-spacing-md);
    }

    .bundle-summary__panel {
      width: 100%;
      padding: var(--hh-spacing-sm) var(--hh-spacing-md);
      background: none;
      border: none;
    }

    .bundle-summary.expanded .bundle-summary__panel {
      border-bottom: 0.1rem solid var(--hh-color-mid-grey);
    }

    .bundle-summary__panel .bundle-summary__progress-bar {
      width: 7.4rem;
      flex: none;
    }

    .bundle-summary__panel {
      display: flex;
      gap: var(--hh-spacing-sm);
      align-items: center;
    }

    .bundle-summary__panel-label {
      display: flex;
      gap: var(--hh-spacing-xs);
      font: var(--hh-body-bold-sm-font);
      letter-spacing: var(--hh-body-bold-sm-letter-spacing);
      flex: none;
    }

    .bundle-summary__panel-label svg {
      width: 1rem;
      height: auto;
    }

    .bundle-summary__panel-title {
      font: var(--hh-body-sm-font);
      letter-spacing: var(--hh-body-sm-letter-spacing);
      text-align: right;
      flex: 1;
    }

    .bundle-summary__message {
      display: none;
    }

    .bundle-summary__box{
      padding: var(--hh-spacing-sm);
      display: flex;
      align-items: center;
      gap: var(--hh-spacing-sm);
      border-radius: 0.4rem;
      border: 0.1rem solid var(--hh-color-mid-grey);
    }

    .bundle-summary__box img {
      flex: none;
    }

    .bundle-summary__box-text {
      font: var(--hh-body-sm-font);
      letter-spacing: var(--hh-body-sm-letter-spacing);
      color: var(--hh-color-brand-subdued);
    }

    .bundle-summary__box-text b {
      font: var(--hh-body-bold-sm-font);
      letter-spacing: var(--hh-body-bold-sm-letter-spacing);
      color: var(--hh-color-night);
    }

    @media (min-width: 1024px) {
      .bundle-summary{
        position: static;
        border: none;
        box-sizing: border-box;
        padding: var(--hh-spacing-lg) var(--hh-spacing-3xl);
      }
      .bundle-summary__wrapper {
        border-radius: 1rem;
        overflow: hidden;
      }
      .bundle-summary__steps {
        max-height: 34rem;
        border: 0.1rem solid var(--hh-color-mid-grey);
        border-radius: 0.4rem;
      }
      .bundle-summary__progress {
        display: none;
        margin-bottom: var(--hh-spacing-md);
      }
      .bundle-summary .bundle-summary__progress {
        display: block;
      }
      .bundle-summary__progress-label{
        text-align: left;
        font: var(--hh-body-bold-sm-font);
        letter-spacing: var(--hh-body-bold-sm-letter-spacing);
        margin-bottom: var(--hh-spacing-xs);
      }
      .bundle-summary__panel {
        display: none;
      }
      .bundle-summary__title {
        display: block;
      }
      .bundle-summary__buttons {
        border: none;
        padding: var(--hh-spacing-md) 0 0;
      }
      .bundle-summary__message {
        display: block;
        background: var(--hh-color-light-green);
        font: var(--hh-body-bold-sm-font);
        color: var(--hh-color-night);
        padding: 1rem var(--hh-spacing-3xl);
        text-align: center;
        margin: 0;
      }
    }
  `
}

customElements.define('hh-bundle-summary', HHBundleSummary)
